/**
 * One WebSocket per tab, reconnecting on its own (ADR 0029).
 *
 * Callers subscribe to topics and get frames; the socket remembers every subscription and
 * replays it after a reconnect, then asks the app to resync what it may have missed. A
 * server close with an application code (4401 unauthenticated, 4404 unknown tenant) stops
 * the retries: those are not network conditions.
 */
import {
  CLOSE_UNAUTHENTICATED,
  CLOSE_UNKNOWN_TENANT,
  type ClientFrame,
  type ServerFrame,
} from "./protocol";

export type SocketStatus = "closed" | "connecting" | "open";
export type FrameHandler = (frame: ServerFrame) => void;

export const PING_INTERVAL_MS = 25_000;
export const PONG_TIMEOUT_MS = 10_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
/** Application close code for "no pong": the browser only allows 1000 or 3000-4999 here. */
const CLOSE_STALE = 4000;

export function defaultSocketUrl(): string {
  const scheme = window.location.protocol === "https:" ? "wss" : "ws";
  return `${scheme}://${window.location.host}/ws/`;
}

export class RealtimeSocket {
  private ws: WebSocket | null = null;
  private status: SocketStatus = "closed";
  private stopped = true;
  private attempt = 0;
  private everOpened = false;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private pongTimer: number | null = null;
  private readonly handlers = new Map<string, Set<FrameHandler>>();
  private readonly statusListeners = new Set<() => void>();
  private readonly resyncListeners = new Set<() => void>();
  private readonly serverCloseListeners = new Set<(code: number) => void>();

  constructor(private readonly url: string = defaultSocketUrl()) {}

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.close(1000);
    }
    this.setStatus("closed");
  }

  getStatus(): SocketStatus {
    return this.status;
  }

  /** `useSyncExternalStore` contract: notify on every status change. */
  readonly subscribeStatus = (listener: () => void): (() => void) => {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  };

  /** Runs after every successful reconnection, once subscriptions are replayed. */
  onResync(listener: () => void): () => void {
    this.resyncListeners.add(listener);
    return () => this.resyncListeners.delete(listener);
  }

  /** Runs when the server closed with an application code and retries stopped. */
  onServerClose(listener: (code: number) => void): () => void {
    this.serverCloseListeners.add(listener);
    return () => this.serverCloseListeners.delete(listener);
  }

  subscribe(topic: string, handler: FrameHandler): () => void {
    let set = this.handlers.get(topic);
    if (!set) {
      set = new Set();
      this.handlers.set(topic, set);
      this.send({ type: "subscribe", topic });
    }
    set.add(handler);
    return () => {
      const current = this.handlers.get(topic);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) {
        this.handlers.delete(topic);
        this.send({ type: "unsubscribe", topic });
      }
    };
  }

  /** A no-op while the socket is not open: presence has no fallback, subscriptions replay. */
  send(frame: ClientFrame): void {
    if (this.status !== "open" || !this.ws) return;
    this.ws.send(JSON.stringify(frame));
  }

  // --- internals ---------------------------------------------------------------------------

  private connect(): void {
    if (this.stopped || this.ws) return;
    this.setStatus("connecting");
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.setStatus("open");
      for (const topic of this.handlers.keys()) this.send({ type: "subscribe", topic });
      this.startPing();
      // The gap between two connections may have swallowed events: refetch what matters.
      // Not on the first connection, which follows the page's own fetches by milliseconds.
      if (this.everOpened) for (const listener of this.resyncListeners) listener();
      this.everOpened = true;
    };
    ws.onmessage = (event: MessageEvent<string>) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data) as ServerFrame;
      } catch {
        return;
      }
      if (frame.type === "pong") {
        this.clearPongTimer();
        return;
      }
      const set = this.handlers.get(frame.topic);
      if (set) for (const handler of set) handler(frame);
    };
    ws.onclose = (event: CloseEvent) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      this.setStatus("closed");
      if (event.code === CLOSE_UNAUTHENTICATED || event.code === CLOSE_UNKNOWN_TENANT) {
        this.stopped = true;
        for (const listener of this.serverCloseListeners) listener(event.code);
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    const base = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.attempt);
    this.attempt += 1;
    const delay = Math.round(base * (0.5 + Math.random()));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startPing(): void {
    this.pingTimer = window.setInterval(() => {
      this.send({ type: "ping" });
      if (this.pongTimer === null) {
        this.pongTimer = window.setTimeout(() => {
          this.pongTimer = null;
          this.ws?.close(CLOSE_STALE); // a sleeping laptop keeps the socket "open" for a while
        }, PONG_TIMEOUT_MS);
      }
    }, PING_INTERVAL_MS);
  }

  private clearPongTimer(): void {
    if (this.pongTimer !== null) {
      window.clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.clearPongTimer();
  }

  private setStatus(status: SocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener();
  }
}
